import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Languages, Loader2 } from 'lucide-react';
import { api } from '../lib/api';

/** Langue cible d'affichage = langue de l'interface (TRAD). */
function uiTarget(lang: string): 'fr' | 'de' {
  return lang === 'de' ? 'de' : 'fr';
}

/**
 * Traduction à la volée d'un texte vers la langue de l'interface (TRAD). Le texte
 * d'origine n'est jamais modifié ; on bascule entre original et traduction. Le
 * bouton n'a de sens que si la langue source diffère de l'interface (`available`).
 */
export function useFlyTranslate(text: string, sourceLang?: string) {
  const { i18n } = useTranslation();
  const target = uiTarget(i18n.language);
  const [out, setOut] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [on, setOn] = useState(false);
  const available = Boolean(sourceLang) && sourceLang !== target;

  // Changement de langue d'interface (ou de texte source) → on repart de l'original
  // et on oublie la traduction précédente (elle visait une autre langue cible).
  useEffect(() => {
    setOn(false);
    setOut(null);
  }, [target, text]);

  const toggle = useCallback(async () => {
    if (on) {
      setOn(false);
      return;
    }
    if (out) {
      setOn(true);
      return;
    }
    setBusy(true);
    try {
      const r = await api.translate(text, target);
      setOut(r.text);
      setOn(true);
    } catch {
      /* on laisse réessayer */
    }
    setBusy(false);
  }, [on, out, text, target]);

  return { display: on && out ? out : text, available, busy, on, toggle };
}

/** Petit bouton « Traduire » / « Voir l'original ». */
export function TranslateButton({ busy, on, onClick }: { busy: boolean; on: boolean; onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      onClick={onClick}
      disabled={busy}
      className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs text-muted transition hover:bg-surface-2 hover:text-fg disabled:opacity-60"
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Languages className="h-3 w-3" />}
      {on ? t('translate.showOriginal') : t('translate.action')}
    </button>
  );
}
